import React, { useState, useEffect, useMemo } from 'react';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import NoDataComponent from '../../../dashboard/no_data_component.jsx';
import WidgetModal from './WidgetModal.jsx';
import { useSelector } from 'react-redux';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { fetchJson } from '../../../../helpers/fetch';
import { LinkToModal } from '../../../common/LinkToModal.jsx';
import StripFormatter, { calculatePresentAttributes, FILLER_TEXT } from '../base/StripFormatter.jsx';
import { IMAGE_DEFAULT_FONT, VALUE_DEFAULT_FONT, NAME_ITEM_DEFAULT_FONT } from '../../settings_sidebar/chart/ChartSettings.jsx';
import { useTaskOptions } from '../../../task/hooks/useTaskOptions.js';
import ShowTaskLink from '../../../task/show/ShowTaskLink.jsx';
import { useProjectFilter } from '../../hooks/useProjectFilter.js';

import {
  task_show_path,
  widget_task_list_json_path
} from '~/routes';

const ATTRIBUTES = ['image', 'name_item', 'value'];

export const taskStatusRefresh = async (settings, queryClient) => {
        await queryClient.invalidateQueries({
            predicate: (query) => {
                const [type, jsonString] = query.queryKey;
                if (type !== 'widget_task_list') return false;
                
                try {
                    const parsedValue = JSON.parse(jsonString);
                    return (settings.variables || []).some(v => JSON.stringify({task_title: [...v.task_title].sort(), created_at: v.created_at, status: v.status}) === JSON.stringify(parsedValue));
                } catch {
                    return false;
                }
            }
        })
}

function TaskStatusWidget({ widgetId, projectId }) {
    const queryClient = useQueryClient();
    const { activeProjectIds, isFiltered } = useProjectFilter();
    const projectFilterEnabled = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.projectFilterEnabled);
    const variables = useSelector(state => state.dashboard.widgets[widgetId].settings?.variables);
    const imageFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.image_fontSize);
    const valueFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.value_fontSize);
    const nameItemFontSize = useSelector(state => state.dashboard.widgets[widgetId].settings?.name_item_fontSize);
    const stacking = useSelector(state => state.dashboard.widgets[widgetId].settings?.stacking) || 'horizontal';
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedVariable, setSelectedVariable] = useState(0);
    const [presentAttributes, setPresentAttributes] = useState({});
    const [localVariables, setLocalVariables] = useState([]);

    const { task_status_options } = useTaskOptions({ projectId });

    const statusMap = useMemo(() => {
        if (!task_status_options) return {};

        return task_status_options.reduce((acc, item) => {
            acc[item.id] = item.label;
            return acc;
        }, {});
    }, [task_status_options]);

    const WIDGET_COLUMN = [
        {
            Header: I18n.t("title"),
            id: "task_title",
            accessor: "title",
            Cell: ({value, row}) => {
                const { identifier, title } = row?.original || {};
                return (
                    <ShowTaskLink
                        identifier={identifier}
                        triggerLabel={title}
                        label={title}
                        projectId={projectId}
                        permissions={{}}
                        advancedFeatures={false}
                    />
                )
        }
        },
        {
            Header: I18n.t("project_identifier"),
            id: "project_identifier",
            accessor: "target_project_identifier",
        },
        {
            Header: I18n.t("identifier"),
            id: "identifier",
            accessor: "identifier",
        },
        {
            Header: I18n.t("created_at"),
            id: "created_at",
            accessor: "created_at",
        },
        {
            Header: I18n.t("status"),
            id: "status",
            accessor: "status",
            Cell: ({value}) => statusMap[value]
        },
    ];

    const effectiveProjectIds = projectFilterEnabled && isFiltered ? activeProjectIds.map(p => p.id) : [];

    const { loading, data, list_data } = useQueries({
        queries: variables.map((v) => ({
            queryKey: ['widget_task_list', JSON.stringify({task_title: [...v.task_title].sort(), created_at: v.created_at, status: v.status}), effectiveProjectIds],
            queryFn: async () => fetchJson(widget_task_list_json_path({ task_title: v.task_title, created_at: v.created_at, status: v.status, project_ids: effectiveProjectIds.length ? effectiveProjectIds : undefined })),
            enabled: Array.isArray(v.task_title)
        })),
        combine: (results) => {
            let data = {};
            let list_data = {}
            results.forEach((result, index) => {
                if (result.isSuccess) {
                    data[index] = result.data?.data.rowCount;
                    list_data[index] = result.data?.data.rows;
                }
            });
            return {loading: results.some(r => r.isLoading), data, list_data}
        }
    });

    const showModal = (e, index) => {
        setIsModalVisible(true);
        setSelectedVariable(index);
    }

    const loadVariableValues = async () => {
       await queryClient.invalidateQueries({
            predicate: (query) => {
                const [type, jsonString] = query.queryKey;
                if (type !== 'widget_task_list') return false;
                
                try {
                    const parsedValue = JSON.parse(jsonString);
                    return (variables || []).some(v => JSON.stringify({task_title: [...v.task_title].sort(), created_at: v.created_at, status: v.status}) === JSON.stringify(parsedValue));
                } catch {
                    return false;
                }
            }
        })
    }

    useEffect(() => {
        if (variables.length > 0){
            const newVariables = variables.map((v,index) => ({
                ...v,
                value: data[index],
            }))
            setLocalVariables(newVariables);
            calculatePresentAttributes(ATTRIBUTES, newVariables, setPresentAttributes);
        }
    }, [variables, data])

    return (
        loading ? <LoadingIndicator /> : 
        !data ? <NoDataComponent /> :
        <div style={{height: '100%', width: '100%'}}>
            <div 
                className="section-counts"
                style={{
                    height: '100%',
                    display: stacking === 'horizontal' ? 'grid' : 'flex',
                    flexDirection: 'column',
                    gridTemplateColumns: `repeat(${localVariables.length}, ${100/localVariables.length}%)`
                }}
            >
                {
                localVariables.map((v, index) => (
                    <div key={index} className="col-xs-2 item"
                        style={{ 
                            backgroundColor: v.background_color, 
                            color: v.text_color ? v.text_color : '#ffffff',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            overflow: 'hidden'
                            }}
                            onClick={(e) => showModal(e,index)}
                            >
                        {
                            stacking === 'horizontal' ? 
                            <>
                                { Object.keys(presentAttributes).includes('image') &&
                                    <p className={"text-center widget-icon adalicon icon-" + (v.image || 'tools')}
                                        style={{"--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`, marginTop: '1rem', color: v.image ? v.text_color : v.background_color}}
                                    ></p>
                                }

                                {Object.keys(presentAttributes).includes('value') &&
                                    <StripFormatter
                                    presentAttributes={presentAttributes}
                                    classNames="qty numscroller"
                                    fontVar={valueFontSize}
                                    str={v.value ?? FILLER_TEXT}
                                    defaultFont={VALUE_DEFAULT_FONT}
                                    color={v.background_color}
                                    attrKey='value'
                                />}
                                {Object.keys(presentAttributes).includes('name_item') &&
                                <StripFormatter
                                    presentAttributes={presentAttributes}
                                    classNames='name'
                                    fontVar={nameItemFontSize}
                                    str={v.name_item}
                                    color={v.background_color}
                                    defaultFont={NAME_ITEM_DEFAULT_FONT}
                                    attrKey='name_item'
                                />
                                }
                            </>
                            :
                            <>
                                {v.image && <p className={"text-center widget-icon adalicon icon-" + v.image} style={{"--font-size": `${imageFontSize || IMAGE_DEFAULT_FONT}px`}}></p>}
                                {v.value && <p className="qty numscroller mt-0" style={{"--font-size": `${valueFontSize || VALUE_DEFAULT_FONT}px`}}>{v.value}</p>}
                                {v.name_item && <p className="name mt-0" style={{"--font-size": `${nameItemFontSize || NAME_ITEM_DEFAULT_FONT}px`}}>{v.name_item}</p>}
                            </>
                        }
                    </div>
                ))}
                {isModalVisible && <WidgetModal 
                    onClose={() => setIsModalVisible(false)} 
                    data = {(list_data || {})[selectedVariable] || []} 
                    columns = {WIDGET_COLUMN}
                    reloadData= {() => loadVariableValues()}
                />}             
            </div>
        </div>
    )

}

export default TaskStatusWidget;
